/*
Copyright 2026 AB Civil Consulting

Simplified annotation context - Slack Huddle Style
- Pen strokes only
- Auto-vanishing after 3 seconds
- Real-time sync via LiveKit data channels
*/

import { logger as rootLogger } from "matrix-js-sdk/lib/logger";

import {
  createContext,
  useContext,
  type FC,
  type ReactNode,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  RoomEvent,
  type Room,
  type RemoteParticipant,
  DataPacket_Kind,
} from "livekit-client";

import {
  type Stroke,
  type Point,
  type AnnotationMessage,
  ANNOTATION_TOPIC,
} from "./types";
import { getUserColor } from "./AnnotationOverlay";
import { isElectron, updateOverlayAnnotations, showOverlay, hideOverlay, setCaptureFrameInfo, notifyScreenShareStarted } from "./electronOverlay";

const logger = rootLogger.getChild("[AnnotationContext]");

const VANISH_DURATION = 6500; // 6.5 seconds (slightly longer than visual fade to ensure smooth transition)

// Pending stroke represents an in-progress remote stroke
interface PendingStroke {
  id: string;
  points: Point[];
  color: string;
  size: number;
  userId: string;
  userName?: string;
  trackId?: string;
  sessionId?: string;
}

interface AnnotationContextValue {
  annotations: Stroke[];
  pendingStrokes: Map<string, PendingStroke>;

  addStroke: (stroke: Stroke) => Promise<void>;
  sendPoints: (strokeId: string, points: Point[], color: string, size: number, trackId?: string, sessionId?: string, tool?: string) => Promise<void>;
  clearAnnotations: (userId?: string, trackId?: string, sessionId?: string) => Promise<void>;
  undoAnnotation: (userId: string, trackId?: string) => Promise<void>;
  requestSync: (trackId?: string, sessionId?: string) => Promise<void>;
  endSession: (trackId: string, sessionId: string) => Promise<void>;
  clearSession: (sessionId: string) => void;

  room: Room | null;
  setRoom: (room: Room | null) => void;
  isConnected: boolean;
  activeTrackId: string | undefined;
  setActiveTrackId: (trackId: string | undefined) => void;
  activeSessionId: string | undefined;
  setActiveSessionId: (sessionId: string | undefined) => void;
}

const AnnotationContext = createContext<AnnotationContextValue | null>(null);

interface AnnotationProviderProps {
  children: ReactNode;
}

export const AnnotationProvider: FC<AnnotationProviderProps> = ({ children }) => {
  const [annotations, setAnnotations] = useState<Stroke[]>([]);
  const [pendingStrokes, setPendingStrokes] = useState<Map<string, PendingStroke>>(new Map());
  const [room, setRoom] = useState<Room | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [activeTrackId, setActiveTrackId] = useState<string | undefined>(undefined);
  const [activeSessionId, setActiveSessionId] = useState<string | undefined>(undefined);

  const roomRef = useRef<Room | null>(null);
  const annotationsRef = useRef<Stroke[]>([]);
  const syncRequestedRef = useRef(false);

  useEffect(() => {
    roomRef.current = room;
    if (room) {
      logger.info("[AnnotationContext] Room set:", room.name, "state:", room.state, "participants:", room.remoteParticipants.size);
    } else {
      logger.info("[AnnotationContext] Room cleared");
    }
  }, [room]);

  useEffect(() => {
    annotationsRef.current = annotations;
    if (annotations.length > 0) {
      logger.info("[AnnotationContext] Annotations ref updated, count:", annotations.length, "ids:", annotations.map(a => a.id.slice(0, 8)));
    }
  }, [annotations]);

  // Auto-remove expired strokes AND orphaned pending strokes
  useEffect(() => {
    logger.info("[AnnotationContext] Starting cleanup interval");
    const interval = setInterval(() => {
      const now = Date.now();

      // Clean up expired completed annotations
      setAnnotations(prev => {
        if (prev.length === 0) return prev;

        const filtered = prev.filter(a => {
          const age = now - a.createdAt;
          const keep = age < VANISH_DURATION;
          if (!keep) {
            logger.info("[AnnotationContext] Removing expired annotation:", a.id.slice(0, 8), "age:", age);
          }
          return keep;
        });

        if (filtered.length !== prev.length) {
          logger.info("[AnnotationContext] Cleanup: removed", prev.length - filtered.length, "annotations,", filtered.length, "remaining");
        }
        return filtered;
      });

      // Clean up orphaned pending strokes (older than 10 seconds)
      setPendingStrokes(prev => {
        if (prev.size === 0) return prev;

        const next = new Map(prev);
        let removed = 0;
        for (const [id, stroke] of next) {
          // Pending strokes don't have createdAt, so check if points have timestamps
          const firstPoint = stroke.points[0];
          if (firstPoint?.t && now - firstPoint.t > 10000) {
            logger.info("[AnnotationContext] Removing orphaned pending stroke:", id.slice(0, 8));
            next.delete(id);
            removed++;
          }
        }
        if (removed > 0) {
          logger.info("[AnnotationContext] Cleaned up", removed, "orphaned pending strokes");
        }
        return removed > 0 ? next : prev;
      });
    }, 500); // Check every 500ms for faster cleanup
    return () => {
      logger.info("[AnnotationContext] Stopping cleanup interval");
      clearInterval(interval);
    };
  }, []);

  // Send annotation data via LiveKit data channel
  const sendAnnotation = useCallback(async (message: AnnotationMessage) => {
    const currentRoom = roomRef.current;
    if (!currentRoom?.localParticipant) {
      logger.warn("[AnnotationContext] Cannot send - no room or localParticipant");
      return;
    }

    // Log comprehensive room state
    const remoteIds = Array.from(currentRoom.remoteParticipants.values()).map(p => p.identity);
    logger.info("[AnnotationContext] === SENDING DATA ===", {
      type: message.type,
      roomName: currentRoom.name,
      roomState: currentRoom.state,
      localIdentity: currentRoom.localParticipant.identity,
      remoteParticipantCount: remoteIds.length,
      remoteParticipants: remoteIds,
    });

    if (currentRoom.state !== "connected") {
      logger.warn("[AnnotationContext] Room not connected! State:", currentRoom.state);
    }

    if (remoteIds.length === 0) {
      logger.warn("[AnnotationContext] No remote participants to send to!");
    }

    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(JSON.stringify(message));
      const isEphemeral = message.type === "points";

      logger.info("[AnnotationContext] Publishing", data.length, "bytes, reliable:", !isEphemeral, "topic:", ANNOTATION_TOPIC);

      await currentRoom.localParticipant.publishData(data, {
        reliable: !isEphemeral,
        topic: ANNOTATION_TOPIC,
      });

      logger.info("[AnnotationContext] Data published successfully:", message.type);
    } catch (e) {
      logger.error("[AnnotationContext] Publish error:", e);
    }
  }, []);

  const clearSession = useCallback((sessionId: string) => {
    logger.info("[AnnotationContext] Clearing session:", sessionId);
    setAnnotations(prev => prev.filter(a => a.sessionId !== sessionId));
    setPendingStrokes(prev => {
      const next = new Map(prev);
      for (const [id, stroke] of next) {
        if (stroke.sessionId === sessionId) next.delete(id);
      }
      return next;
    });
  }, []);

  const endSession = useCallback(async (trackId: string, sessionId: string) => {
    const currentRoom = roomRef.current;
    if (!currentRoom?.localParticipant) return;

    logger.info("[AnnotationContext] Ending session:", sessionId);
    clearSession(sessionId);

    await sendAnnotation({
      type: "session-end",
      trackId,
      sessionId,
      userId: currentRoom.localParticipant.identity,
      timestamp: Date.now(),
    });
  }, [sendAnnotation, clearSession]);

  const addStroke = useCallback(async (stroke: Stroke) => {
    logger.info("[AnnotationContext] Adding local stroke:", stroke.id);
    setAnnotations(prev => [...prev, stroke]);
    await sendAnnotation({ type: "stroke", stroke });
  }, [sendAnnotation]);

  const sendPoints = useCallback(async (strokeId: string, points: Point[], color: string, size: number, trackId?: string, sessionId?: string, tool?: string) => {
    const currentRoom = roomRef.current;
    if (!currentRoom?.localParticipant) return;

    await sendAnnotation({
      type: "points",
      strokeId,
      points,
      userId: currentRoom.localParticipant.identity,
      color,
      size,
      trackId,
      sessionId,
      tool,
    });
  }, [sendAnnotation]);

  const clearAnnotations = useCallback(async (userId?: string, trackId?: string, sessionId?: string) => {
    if (userId) {
      setAnnotations(prev => prev.filter(a => {
        if (sessionId && a.sessionId !== sessionId) return true;
        if (trackId && a.trackId !== trackId) return true;
        return a.userId !== userId;
      }));
    } else {
      if (sessionId) {
        setAnnotations(prev => prev.filter(a => a.sessionId !== sessionId));
      } else {
        setAnnotations([]);
      }
    }
    await sendAnnotation({ type: "clear", userId, trackId, sessionId });
  }, [sendAnnotation]);

  const undoAnnotation = useCallback(async (userId: string, trackId?: string) => {
    setAnnotations(prev => {
      const userAnnotations = prev.filter(a => {
        if (a.userId !== userId) return false;
        if (trackId && a.trackId !== trackId) return false;
        return true;
      });
      if (userAnnotations.length === 0) return prev;
      const lastAnnotation = userAnnotations[userAnnotations.length - 1];
      return prev.filter(a => a.id !== lastAnnotation.id);
    });
    await sendAnnotation({ type: "undo", userId, trackId });
  }, [sendAnnotation]);

  const requestSync = useCallback(async (trackId?: string, sessionId?: string) => {
    if (syncRequestedRef.current) return;
    syncRequestedRef.current = true;
    logger.info("[AnnotationContext] Requesting sync");
    await sendAnnotation({ type: "sync-request", trackId, sessionId });
    setTimeout(() => { syncRequestedRef.current = false; }, 5000);
  }, [sendAnnotation]);

  const isDesignatedResponder = useCallback((currentRoom: Room): boolean => {
    const localId = currentRoom.localParticipant?.identity;
    if (!localId) return false;
    const identities = [
      localId,
      ...Array.from(currentRoom.remoteParticipants.values()).map(p => p.identity),
    ].sort();
    return identities[0] === localId;
  }, []);

  // Set up LiveKit data channel listener
  useEffect(() => {
    if (!room) {
      logger.info("[AnnotationContext] No room available");
      setIsConnected(false);
      return;
    }

    logger.info("[AnnotationContext] ========= SETTING UP LISTENERS =========");
    logger.info("[AnnotationContext] Room name:", room.name);
    logger.info("[AnnotationContext] Room state:", room.state);
    logger.info("[AnnotationContext] Local participant:", room.localParticipant?.identity);
    logger.info("[AnnotationContext] Remote participants:", Array.from(room.remoteParticipants.values()).map(p => p.identity));
    setIsConnected(true);

    // Also subscribe to all data events to see what's coming through
    const handleAnyData = (...args: unknown[]): void => {
      logger.info("[AnnotationContext] RAW DataReceived event args:", args.length, args.map(a => typeof a));
    };

    const handleDataReceived = (
      payload: Uint8Array,
      participant: RemoteParticipant | undefined,
      _kind: DataPacket_Kind | undefined,
      topic?: string
    ) => {
      logger.info("[AnnotationContext] === DATA RECEIVED ===");
      logger.info("[AnnotationContext] Topic:", topic);
      logger.info("[AnnotationContext] From participant:", participant?.identity || "unknown");
      logger.info("[AnnotationContext] Expected topic:", ANNOTATION_TOPIC);

      if (topic !== ANNOTATION_TOPIC) {
        logger.info("[AnnotationContext] Ignoring - topic mismatch");
        return;
      }

      try {
        const decoder = new TextDecoder();
        const messageStr = decoder.decode(payload);
        logger.info("[AnnotationContext] Raw message:", messageStr.substring(0, 200));

        const message: AnnotationMessage = JSON.parse(messageStr);
        logger.info("[AnnotationContext] Parsed message type:", message.type);

        switch (message.type) {
          case "stroke":
            if (message.stroke) {
              const now = Date.now();
              logger.info("[AnnotationContext] === RECEIVED STROKE ===", {
                from: message.stroke.userId,
                strokeId: message.stroke.id,
                color: message.stroke.color,
                originalCreatedAt: message.stroke.createdAt,
                newCreatedAt: now,
              });
              // Remove pending stroke by BOTH strokeId AND userId
              setPendingStrokes(prev => {
                const next = new Map(prev);
                // Remove by stroke ID
                if (next.has(message.stroke.id)) {
                  logger.info("[AnnotationContext] Removing pending stroke by ID:", message.stroke.id.slice(0, 8));
                  next.delete(message.stroke.id);
                }
                // Also remove any other pending strokes from same user
                for (const [id, stroke] of next) {
                  if (stroke.userId === message.stroke.userId) {
                    logger.info("[AnnotationContext] Removing pending stroke by userId:", id.slice(0, 8));
                    next.delete(id);
                  }
                }
                logger.info("[AnnotationContext] Pending strokes remaining:", next.size);
                return next;
              });
              // Add the completed stroke with FRESH createdAt for proper fade on receiver
              const receivedStroke: Stroke = {
                ...message.stroke,
                createdAt: now, // Reset to receiver's current time for proper fade
              };
              logger.info("[AnnotationContext] Adding stroke with createdAt:", receivedStroke.createdAt);
              setAnnotations(prev => [...prev, receivedStroke]);
            }
            break;

          case "points":
            if (message.strokeId && message.points && message.userId) {
              logger.info("[AnnotationContext] Adding pending points from:", message.userId, "color:", message.color, "count:", message.points.length);
              setPendingStrokes(prev => {
                const next = new Map(prev);
                const existing = next.get(message.strokeId);
                // Use color and size from message (sent by the drawer)
                if (existing) {
                  next.set(message.strokeId, {
                    ...existing,
                    points: [...existing.points, ...message.points],
                  });
                } else {
                  next.set(message.strokeId, {
                    id: message.strokeId,
                    points: message.points,
                    color: message.color, // Use sender's color
                    size: message.size,   // Use sender's size
                    userId: message.userId,
                    trackId: message.trackId,
                    sessionId: message.sessionId,
                  });
                }
                return next;
              });
            }
            break;

          case "clear":
            logger.info("[AnnotationContext] Processing clear from:", message.userId);
            if (message.userId) {
              setAnnotations(prev => prev.filter(a => {
                if (message.sessionId && a.sessionId !== message.sessionId) return true;
                if (message.trackId && a.trackId !== message.trackId) return true;
                return a.userId !== message.userId;
              }));
            } else if (message.sessionId) {
              setAnnotations(prev => prev.filter(a => a.sessionId !== message.sessionId));
            } else {
              setAnnotations([]);
            }
            break;

          case "undo":
            if (message.userId) {
              logger.info("[AnnotationContext] Processing undo from:", message.userId);
              setAnnotations(prev => {
                const userAnnotations = prev.filter(a => {
                  if (a.userId !== message.userId) return false;
                  if (message.trackId && a.trackId !== message.trackId) return false;
                  return true;
                });
                if (userAnnotations.length === 0) return prev;
                const lastAnnotation = userAnnotations[userAnnotations.length - 1];
                return prev.filter(a => a.id !== lastAnnotation.id);
              });
            }
            break;

          case "sync-request":
            logger.info("[AnnotationContext] Received sync request");
            if (room && isDesignatedResponder(room) && annotationsRef.current.length > 0) {
              const toSend = message.sessionId
                ? annotationsRef.current.filter(a => a.sessionId === message.sessionId)
                : annotationsRef.current;
              logger.info("[AnnotationContext] Responding with", toSend.length, "strokes");
              void sendAnnotation({
                type: "sync-response",
                strokes: toSend,
                sessionId: message.sessionId,
              });
            }
            break;

          case "sync-response":
            if (message.strokes && message.strokes.length > 0) {
              logger.info("[AnnotationContext] Received sync response with", message.strokes.length, "strokes");
              setAnnotations(prev => {
                const existingIds = new Set(prev.map(a => a.id));
                const newStrokes = message.strokes.filter(s => !existingIds.has(s.id));
                return [...prev, ...newStrokes].sort((a, b) => a.createdAt - b.createdAt);
              });
            }
            break;

          case "session-end":
            if (message.sessionId) {
              logger.info("[AnnotationContext] Session ended:", message.sessionId);
              setAnnotations(prev => prev.filter(a => a.sessionId !== message.sessionId));
              setPendingStrokes(prev => {
                const next = new Map(prev);
                for (const [id, stroke] of next) {
                  if (stroke.sessionId === message.sessionId) next.delete(id);
                }
                return next;
              });
            }
            break;
        }
      } catch (e) {
        logger.error("[AnnotationContext] Parse error:", e);
      }
    };

    // Attach the listener
    room.on(RoomEvent.DataReceived, handleDataReceived);
    room.on(RoomEvent.DataReceived, handleAnyData);
    logger.info("[AnnotationContext] DataReceived listener attached");

    // Also listen for participant changes
    const handleParticipantConnected = (participant: RemoteParticipant): void => {
      logger.info("[AnnotationContext] Participant connected:", participant.identity);
    };
    const handleParticipantDisconnected = (participant: RemoteParticipant): void => {
      logger.info("[AnnotationContext] Participant disconnected:", participant.identity);
    };
    room.on(RoomEvent.ParticipantConnected, handleParticipantConnected);
    room.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);

    // Log room info
    logger.info("[AnnotationContext] Room info:", {
      name: room.name,
      state: room.state,
      localIdentity: room.localParticipant?.identity,
      remoteParticipants: Array.from(room.remoteParticipants.values()).map(p => p.identity),
    });

    return () => {
      logger.info("[AnnotationContext] Removing DataReceived listener");
      room.off(RoomEvent.DataReceived, handleDataReceived);
      room.off(RoomEvent.DataReceived, handleAnyData);
      room.off(RoomEvent.ParticipantConnected, handleParticipantConnected);
      room.off(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
      setIsConnected(false);
    };
  }, [room, isDesignatedResponder, sendAnnotation]);

// Send annotations to Electron overlay whenever they change
  useEffect(() => {
    console.log("[AnnotationContext] === OVERLAY USEEFFECT RUNNING ===", {
      annotationsCount: annotations.length,
      pendingCount: pendingStrokes.size
    });
    
    // Convert pending strokes Map to array for serialization
    const pendingArray = Array.from(pendingStrokes.values());
    
    // Send update to overlay
    console.log("[AnnotationContext] Calling updateOverlayAnnotations...");
    updateOverlayAnnotations(annotations, pendingArray);
    console.log("[AnnotationContext] updateOverlayAnnotations called");
    
    if (annotations.length > 0 || pendingArray.length > 0) {
      logger.info("[AnnotationContext] Sent to overlay:", annotations.length, "strokes,", pendingArray.length, "pending");
    }
  }, [annotations, pendingStrokes]);

  // Video detection for Electron overlay - runs when annotations are active
  useEffect(() => {
    // Run if we have direct Electron access OR we are in an iframe (Element Desktop)
    const inIframe = window.self !== window.top;
    if (!isElectron() && !inIframe) return;
    
    logger.info("[AnnotationContext] Video detection starting...");
    let dimensionsSent = false;
    
    const findVideo = (): HTMLVideoElement | null => {
      // Method 1: Search for any video element
      const v1 = document.querySelector("video") as HTMLVideoElement | null;
      if (v1) {
        logger.info("[AnnotationContext] Found video via document.querySelector");
        return v1;
      }
      
      // Method 2: Search in iframes
      const iframes = document.querySelectorAll("iframe");
      for (const iframe of iframes) {
        try {
          const v = iframe.contentDocument?.querySelector("video") as HTMLVideoElement | null;
          if (v) {
            logger.info("[AnnotationContext] Found video in iframe");
            return v;
          }
        } catch (e) {
          // Cross-origin iframe
        }
      }
      
      logger.info("[AnnotationContext] No video found");
      return null;
    };
    
    const checkAndSendDimensions = (): void => {
      if (dimensionsSent) return;
      
      const video = findVideo();
      if (video) {
        const vw = video.videoWidth;
        const vh = video.videoHeight;
        
        logger.info("[AnnotationContext] Video dimensions:", vw, "x", vh);
        
        if (vw && vh) {
          logger.info("[AnnotationContext] SENDING dimensions to Electron:", vw, "x", vh);
          setCaptureFrameInfo(vw, vh);
          notifyScreenShareStarted({ frameWidth: vw, frameHeight: vh });
          showOverlay();
          dimensionsSent = true;
        }
      }
    };
    
    // Check immediately
    checkAndSendDimensions();
    
    // Keep checking until we send dimensions
    const interval = setInterval(() => {
      if (!dimensionsSent) {
        checkAndSendDimensions();
      }
    }, 500);
    
    return () => {
      clearInterval(interval);
    };
  }, []);

  // Request sync when connected with other participants
  useEffect(() => {
    if (isConnected && room && room.remoteParticipants.size > 0) {
      logger.info("[AnnotationContext] Requesting sync, remote participants:", room.remoteParticipants.size);
      const timer = setTimeout(() => {
        void requestSync(activeTrackId, activeSessionId);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isConnected, room, requestSync, activeTrackId, activeSessionId]);

  return (
    <AnnotationContext.Provider value={{
      annotations,
      pendingStrokes,
      addStroke,
      sendPoints,
      clearAnnotations,
      undoAnnotation,
      requestSync,
      endSession,
      clearSession,
      room,
      setRoom,
      isConnected,
      activeTrackId,
      setActiveTrackId,
      activeSessionId,
      setActiveSessionId,
    }}>
      {children}
    </AnnotationContext.Provider>
  );
};

export const useAnnotation = () => {
  const context = useContext(AnnotationContext);
  if (!context) {
    throw new Error("useAnnotation must be used within AnnotationProvider");
  }
  return context;
};

export const useAnnotationOptional = () => {
  return useContext(AnnotationContext);
};

interface AnnotationRoomSetterProps {
  room: Room | null;
}

export const AnnotationRoomSetter: FC<AnnotationRoomSetterProps> = ({ room }) => {
  const ctx = useAnnotationOptional();

  useEffect(() => {
    if (ctx) {
      logger.info("[AnnotationRoomSetter] Setting room:", room?.name || "null", "state:", room?.state || "N/A");
      ctx.setRoom(room);
    }
  }, [ctx, room]);

  return null;
};
